import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Section, Container, Typography, Grid, Anchor, Button } from 'components/base';
import {
  BlogCarousel,
  CategoryCard,
  CloudinaryPicture,
  ProductCard,
  PrismicHtmlContent,
} from 'components/rich';
import Icon, { ICON_TYPE } from 'components/icon';
import HeroBanner from './heroBanner';

import SETTINGS from '../../settings';
import { ProductDetailLink, CategoryPageLink, AboutPageLink } from '../pageLinks';

export const ProductsSection = props => {
  const colProps = {
    xs: props.xs,
    sm: props.sm,
    md: props.md,
    lg: props.lg,
    xl: props.xl,
  };

  const cardClassName = (index, disabled = false) =>
    classNames({
      'card-equalize': true,
      'card-mycanvas': true,
      'card-mobile-compact': colProps.xs === 6,
      [`card-color-variant-${index % 4}`]: true,
      'card-img-square': true,
      "card-mycanvas-nohover": disabled,
    });

  return (
    <Section>
      <Container>
        {props.title && (
          <Typography.H2 className="section-title mb-2">
            {props.title.toLowerCase().indexOf('ancestry') >= 0 && (
              <>
                <Icon type={ICON_TYPE.ANCESTRY} className="text-ancestry" size={40} />{' '}
              </>
            )}
            {props.title.toLowerCase().indexOf('familysearch') >= 0 && (
              <>
                <Icon type={ICON_TYPE.FAMILY_SEARCH} className="text-familysearch" size={38} />{' '}
              </>
            )}
            {props.title}
          </Typography.H2>
        )}
        <Grid.Row>
          {props.subCategories.map((category, index) => (
            <Grid.Col key={category.id} {...colProps}>
              <CategoryPageLink category={category}>
                <CategoryCard asLink category={category} className={cardClassName(index)} />
              </CategoryPageLink>
            </Grid.Col>
          ))}
          {props.products.map((product, index) => (
            <Grid.Col key={product.id} {...colProps}>
              <ProductDetailLink productId={product.id} disabled={product.config?.disabled === true}>
                <ProductCard asLink={product.config?.disabled !== true} product={product} className={cardClassName(index, product.config?.disabled === true)} />
              </ProductDetailLink>
            </Grid.Col>
          ))}
        </Grid.Row>
      </Container>
    </Section>
  );
};

ProductsSection.propTypes = {
  title: PropTypes.string,
  subCategories: PropTypes.array,
  products: PropTypes.array,
  xs: PropTypes.number,
  sm: PropTypes.number,
  md: PropTypes.number,
  lg: PropTypes.number,
  xl: PropTypes.number,
};

ProductsSection.defaultProps = {
  subCategories: [],
  products: [],
};

export const CategoriesSection = props => {
  const colProps = {
    xs: props.xs,
    sm: props.sm,
    md: props.md,
    lg: props.lg,
    xl: props.xl,
  };

  const cardClassName = index =>
    classNames({
      'card-equalize': true,
      'card-mycanvas': true,
      [`card-color-variant-${index % 4}`]: true,
    });

  return (
    <Section>
      <Container>
        <Typography.H2 className="section-title mb-2">{props.title}</Typography.H2>
        <Grid.Row>
          {props.categories.map((category, index) => (
            <Grid.Col key={category.id} {...colProps}>
              <CategoryPageLink category={category}>
                <CategoryCard asLink category={category} className={cardClassName(index)} />
              </CategoryPageLink>
            </Grid.Col>
          ))}
        </Grid.Row>
      </Container>
    </Section>
  );
};

CategoriesSection.propTypes = {
  title: PropTypes.string,
  categories: PropTypes.array,
  xs: PropTypes.number,
  sm: PropTypes.number,
  md: PropTypes.number,
  lg: PropTypes.number,
  xl: PropTypes.number,
};

export const MottoSection = props => (
  <Section type={props.type}>
    <Container>
      <Grid.Row>
        <Grid.Col xs={12} md={9} className="offset-md-1">
          <span className="text-primary font-weight-bold">{props.label}</span>
          <Typography.H2 className="mt-1 mb-6">{props.motto}</Typography.H2>
          <AboutPageLink>
            <Anchor className="btn btn-primary btn-lg">Learn More</Anchor>
          </AboutPageLink>
        </Grid.Col>
      </Grid.Row>
    </Container>
  </Section>
);

MottoSection.propTypes = {
  type: PropTypes.oneOf(['green', 'orange', 'brown', 'blue']),
  label: PropTypes.string,
  motto: PropTypes.string,
};

export const GetStartedSection = props => (
  <Section className="my-5 py-0">
    <HeroBanner
      title="Family History"
      description="Create custom family history books and posters. Include family tree charts, timelines, family photos and more."
      inverted
      className="py-5 text-right"
      button={
        <CategoryPageLink categoryId="family-history">
          <Anchor className="btn btn-primary btn-lg">Get Started</Anchor>
        </CategoryPageLink>
      }
    >
      <CloudinaryPicture imageKey="categories/famhistorymc" className="w-100" />
    </HeroBanner>
  </Section>
);

export const ReferencesSection = () => (
  <Section className="container">
    <Typography.H2 className="section-title mb-2">AS FEATURED IN</Typography.H2>
    <Grid.Row className="mt-6">
      <Grid.Col xs={12} md={6} lg={4} className="col-logo">
        <img src="static/img/logo-new-york-times.png" alt="" />
      </Grid.Col>
      <Grid.Col xs={12} md={6} lg={4} className="col-logo">
        <img src="static/img/logo-good-housekeeping.png" alt="" />
      </Grid.Col>
      <Grid.Col xs={12} md={6} lg={4} className="col-logo">
        <img src="static/img/logo-brit-and-co.png" alt="" />
      </Grid.Col>
      <Grid.Col xs={12} md={6} lg={4} className="col-logo">
        <img src="static/img/logo-parents.png" alt="" />
      </Grid.Col>
      <Grid.Col xs={12} md={6} lg={4} className="col-logo">
        <img src="static/img/logo-better-homes-gardens.png" alt="" />
      </Grid.Col>
      <Grid.Col xs={12} md={6} lg={4} className="col-logo">
        <img src="static/img/logo-refinery29.png" alt="" />
      </Grid.Col>
    </Grid.Row>
  </Section>
);

export const InstagramSection = () => (
  <Section type="brown" className="my-5">
    <Container>
      <Typography.H3 className="section-title">#print_mycanvas</Typography.H3>
      <div className="text-center">
        <Typography.P className="text-black mb-3">
          Follow us for genealogy tips, MyCanvas updates and family history in print.
        </Typography.P>
        <Anchor href={SETTINGS.INSTAGRAM_URL} targetBlank className="link-arrow text-brown">
          FOLLOW US ON INSTAGRAM
        </Anchor>
      </div>
      <Grid.Row className="mt-5 row-instagram">
        <Grid.Col xs={6} md={3}>
          <Anchor href={SETTINGS.INSTAGRAM_URL} targetBlank className="img-instagram-container">
            <CloudinaryPicture imageKey="instagram/mcinstagram1" />
          </Anchor>
        </Grid.Col>
        <Grid.Col xs={6} md={3}>
          <Anchor href={SETTINGS.INSTAGRAM_URL} targetBlank className="img-instagram-container">
            <CloudinaryPicture imageKey="instagram/mcinstagram2" />
          </Anchor>
        </Grid.Col>
        <Grid.Col xs={6} md={3}>
          <Anchor href={SETTINGS.INSTAGRAM_URL} targetBlank className="img-instagram-container">
            <CloudinaryPicture imageKey="instagram/mcinstagram3" />
          </Anchor>
        </Grid.Col>
        <Grid.Col xs={6} md={3}>
          <Anchor href={SETTINGS.INSTAGRAM_URL} targetBlank className="img-instagram-container">
            <CloudinaryPicture imageKey="instagram/mcinstagram4" />
          </Anchor>
        </Grid.Col>
      </Grid.Row>
    </Container>
  </Section>
);

export const BlogSection = props => (
  <Section>
    <Container>
      <Typography.H2 className="section-title mb-2">FROM OUR BLOG</Typography.H2>
      <BlogCarousel className="carousel-blog">
        {props.data.map((article, idx) => (
          <BlogCarousel.Item
            key={article.uid}
            index={idx}
            imageKey={article.imageKey}
            title={article.caption}
            url={article.link}
          >
            <div className="card-text text-black">
              <PrismicHtmlContent content={article.description} />
            </div>
            <span className="link-arrow">READ MORE</span>
          </BlogCarousel.Item>
        ))}
      </BlogCarousel>
      <div className="text-center">
        <Button type="outline" asLink href="/blog">
          View All Articles
        </Button>
      </div>
    </Container>
  </Section>
);

BlogSection.propTypes = {
  data: PropTypes.array,
};

export const NewsletterSection = props => {
  return (
    <Section type="green" className={props.className}>
      <Container className="text-center">
        <Typography.H2 className="section-title mb-2">
          Subscribe to the MyCanvas Newsletter
        </Typography.H2>
        <Typography.P className="text-black mt-2 mb-5">
          Be the first to know about site updates, new products and more!
        </Typography.P>

        <Button asLink href={SETTINGS.NEWSLETTER_URL} targetBlank type="primary">
          Subscribe To Our Newsletter
        </Button>
      </Container>
    </Section>
  );
};

NewsletterSection.propTypes = {
  className: PropTypes.string,
};
